import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { setPageSeo } from '../utils/seo';
import { fadeUp, staggerContainer, EASE_OUT_EXPO } from '../components/motion/variants';

const MotionLink = motion.create(Link);

/* ─── tiny hook: intersection observer (AnimatedCounter için) ─── */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

/* ─── Animated counter ─── */
function AnimatedCounter({ value }) {
  const [display, setDisplay] = useState('0');
  const [ref, visible] = useInView();
  useEffect(() => {
    if (!visible) return;
    const numericPart = value.replace(/[^\d.]/g, '');
    const prefix = value.replace(/[\d.,+]+.*/, '');
    const suffix = value.replace(/^[^0-9]*[\d.,]+/, '');
    const num = parseFloat(numericPart);
    if (isNaN(num)) { setDisplay(value); return; }
    const duration = 1800;
    const startTs = performance.now();
    const step = (now) => {
      const elapsed = now - startTs;
      const progress = Math.min(elapsed / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(ease * num * 10) / 10;
      setDisplay(`${prefix}${current % 1 === 0 ? current.toLocaleString('tr') : current.toLocaleString('tr', { minimumFractionDigits: 1 })}${suffix}`);
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [visible, value]);
  return <span ref={ref}>{display}</span>;
}

/* ─── Feature Card ─── */
function FeatureCard({ feature, index }) {
  const isEven = index % 2 === 0;
  const reduceMotion = useReducedMotion();

  return (
    <motion.div
      className="feature-row"
      style={{ flexDirection: isEven ? 'row' : 'row-reverse' }}
      initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 48 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, ease: EASE_OUT_EXPO }}
    >
      {/* Text Side */}
      <div className="feature-text">
        {feature.badge && (
          <span className="feature-badge" style={{ background: feature.color + '22', color: feature.color }}>
            <span className="material-symbols-outlined" style={{ fontSize: 13 }}>bolt</span>
            {feature.badge}
          </span>
        )}
        <div className="feature-icon-wrap" style={{ background: feature.color + '18' }}>
          <span className="material-symbols-outlined" style={{ color: feature.color, fontSize: '2.5rem' }}>
            {feature.icon}
          </span>
        </div>
        <h3 className="feature-title">{feature.title}</h3>
        <p className="feature-subtitle" style={{ color: feature.color }}>{feature.subtitle}</p>
        <p className="feature-desc">{feature.desc}</p>
        <ul className="feature-bullets">
          {feature.bullets.map((b, i) => (
            <li key={i} style={{ '--bullet-color': feature.color }}>
              <span className="material-symbols-outlined bullet-icon">check_circle</span>
              {b}
            </li>
          ))}
        </ul>
        {feature.link && (
          <MotionLink
            to={feature.link}
            whileHover={{ y: -2, backgroundColor: feature.color + '30' }}
            whileTap={{ scale: 0.97 }}
            transition={{ duration: 0.2 }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 20,
              padding: '12px 24px',
              borderRadius: 14,
              background: feature.color + '20',
              border: `1px solid ${feature.color}40`,
              color: feature.color,
              fontWeight: 800,
              fontSize: 14,
              textDecoration: 'none',
            }}
          >
            Stok Modülünü Detaylı İncele
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_forward</span>
          </MotionLink>
        )}
      </div>

      {/* Mock Screen */}
      <motion.div
        className="feature-screen-wrap"
        whileHover={reduceMotion ? undefined : { y: -6 }}
        transition={{ duration: 0.3, ease: EASE_OUT_EXPO }}
      >
        <div className="feature-screen" style={{ '--accent': feature.color }}>
          <MockScreen id={feature.id} color={feature.color} />
        </div>
        <div className="screen-glow" style={{ background: feature.color + '30' }} />
      </motion.div>
    </motion.div>
  );
}

/* ─── Mock UI screens per feature ─── */
function MockScreen({ id, color }) {
  if (id === 'qrmenu') return (
    <div className="mock mock-qrmenu">
      <div className="mock-header">
        <div className="mock-dot" /> <div className="mock-dot" /> <div className="mock-dot" />
        <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>Digital Menu</span>
      </div>
      <div style={{ display: 'flex', gap: 8, padding: '8px 12px', borderBottom: '1px solid #ffffff10' }}>
        {['Starters', 'Mains', 'Drinks', 'Desserts'].map((c, i) => (
          <span key={c} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 20, background: i === 1 ? color : '#ffffff08', color: i === 1 ? '#fff' : '#888', whiteSpace: 'nowrap' }}>{c}</span>
        ))}
      </div>
      <div style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {[
          { name: 'Grilled Steak', price: '₺245', cal: '520 kcal' },
          { name: 'Mixed Grill', price: '₺195', cal: '680 kcal' },
          { name: 'Lamb Chops', price: '₺285', cal: '490 kcal' },
        ].map((item) => (
          <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 12, background: '#ffffff06' }}>
            <div style={{ width: 48, height: 48, borderRadius: 10, background: color + '20', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span className="material-symbols-outlined" style={{ color, fontSize: 22 }}>skillet</span>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{item.name}</div>
              <div style={{ fontSize: 11, color: '#888' }}>{item.cal}</div>
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color }}>{item.price}</div>
          </div>
        ))}
      </div>
    </div>
  );

  if (id === 'reservations') return (
    <div className="mock">
      <div className="mock-header">
        <div className="mock-dot" /> <div className="mock-dot" /> <div className="mock-dot" />
        <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>Reservation Management</span>
      </div>
      <div style={{ padding: '10px 12px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, marginBottom: 12 }}>
          {[
            { n: 'M1', s: 'ok' }, { n: 'M2', s: 'busy' }, { n: 'M3', s: 'ok' },
            { n: 'M4', s: 'res' }, { n: 'M5', s: 'ok' }, { n: 'M6', s: 'busy' },
            { n: 'M7', s: 'ok' }, { n: 'M8', s: 'res' }, { n: 'M9', s: 'ok' }, { n: 'M10', s: 'ok' },
          ].map((t) => {
            const bg = t.s === 'ok' ? '#10b98130' : t.s === 'busy' ? '#ef444430' : '#f9731630';
            const bc = t.s === 'ok' ? '#10b981' : t.s === 'busy' ? '#ef4444' : '#f97316';
            return (
              <div key={t.n} style={{ aspectRatio: '1', borderRadius: 8, background: bg, border: `1px solid ${bc}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: bc, fontWeight: 700 }}>{t.n}</div>
            );
          })}
        </div>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>Today's Reservations</div>
        {[
          { name: 'John D.', time: '19:00', pax: '4 pax' },
          { name: 'Jane S.', time: '20:30', pax: '2 pax' },
          { name: 'Alice B.', time: '21:00', pax: '6 pax' },
        ].map((r) => (
          <div key={r.name} style={{ display: 'flex', alignItems: 'center', padding: '7px 8px', borderRadius: 8, background: '#ffffff05', marginBottom: 4 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 8 }}>
              <span className="material-symbols-outlined" style={{ color, fontSize: 14 }}>person</span>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, color: '#fff', fontWeight: 600 }}>{r.name}</div>
              <div style={{ fontSize: 10, color: '#888' }}>{r.pax}</div>
            </div>
            <span style={{ fontSize: 12, color, fontWeight: 700 }}>{r.time}</span>
          </div>
        ))}
      </div>
    </div>
  );

  if (id === 'kitchen') return (
    <div className="mock">
      <div className="mock-header">
        <div className="mock-dot" /> <div className="mock-dot" /> <div className="mock-dot" />
        <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>Kitchen Display (KDS)</span>
        <span style={{ marginLeft: 'auto', fontSize: 10, background: '#10b98120', color: '#10b981', padding: '2px 8px', borderRadius: 20 }}>● Live</span>
      </div>
      <div style={{ padding: '8px 10px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {[
          { table: 'Table 3', items: ['Grilled Chicken', 'Rice'], time: '4:23', status: 'Cooking', sc: '#f97316' },
          { table: 'Table 7', items: ['Lentil Soup', 'Meatball', 'Ayran'], time: '2:10', status: 'Waiting', sc: '#ef4444' },
          { table: 'Table 1', items: ['Burger'], time: '7:45', status: 'Ready', sc: '#10b981' },
          { table: 'Table 5', items: ['Pizza', 'Cola'], time: '1:30', status: 'Waiting', sc: '#ef4444' },
        ].map((o) => (
          <div key={o.table} style={{ background: '#ffffff06', borderRadius: 10, padding: 10, borderTop: `3px solid ${o.sc}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>{o.table}</span>
              <span style={{ fontSize: 12, color: o.sc, fontWeight: 700 }}>{o.time}</span>
            </div>
            {o.items.map(i => <div key={i} style={{ fontSize: 11, color: '#aaa', marginBottom: 2 }}>• {i}</div>)}
            <div style={{ marginTop: 6, fontSize: 10, background: o.sc + '20', color: o.sc, padding: '2px 8px', borderRadius: 20, display: 'inline-block' }}>{o.status}</div>
          </div>
        ))}
      </div>
    </div>
  );

  if (id === 'pos') return (
    <div className="mock">
      <div className="mock-header">
        <div className="mock-dot" /> <div className="mock-dot" /> <div className="mock-dot" />
        <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>Order & POS</span>
      </div>
      <div style={{ display: 'flex', gap: 0, height: 'calc(100% - 36px)' }}>
        <div style={{ width: '42%', borderRight: '1px solid #ffffff10', padding: 8 }}>
          <div style={{ fontSize: 10, color: '#888', marginBottom: 6 }}>Tables</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
            {['T1','T2','T3','T4','T5','T6'].map((m, i) => (
              <div key={m} style={{ padding: '8px 4px', borderRadius: 8, background: i === 1 ? color + '20' : '#ffffff06', border: i === 1 ? `1px solid ${color}60` : '1px solid transparent', textAlign: 'center', fontSize: 12, color: i === 1 ? color : '#888', fontWeight: 700, cursor: 'pointer' }}>{m}</div>
            ))}
          </div>
        </div>
        <div style={{ flex: 1, padding: 8, display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 10, color: '#888', marginBottom: 6 }}>Table 2 – Order</div>
          {[['Iskender', 1, '₺185'], ['Ayran', 2, '₺40'], ['Kunefe', 1, '₺85']].map(([n, q, p]) => (
            <div key={n} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #ffffff08', fontSize: 11 }}>
              <span style={{ color: '#ccc' }}>{n}</span>
              <span style={{ color: '#888' }}>x{q}</span>
              <span style={{ color }}>{p}</span>
            </div>
          ))}
          <div style={{ marginTop: 'auto', padding: '8px 0 0', borderTop: '1px solid #ffffff15' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, color: '#fff' }}>
              <span>Total</span><span style={{ color }}>₺310</span>
            </div>
            <div style={{ marginTop: 6, display: 'flex', gap: 4 }}>
              <div style={{ flex: 1, padding: '5px', borderRadius: 6, background: color, textAlign: 'center', fontSize: 10, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Pay</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (id === 'analytics') return (
    <div className="mock">
      <div className="mock-header">
        <div className="mock-dot" /> <div className="mock-dot" /> <div className="mock-dot" />
        <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>Analytics & Reports</span>
      </div>
      <div style={{ padding: '8px 12px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 10 }}>
          {[['₺12.450', 'Daily Rev.', '#f97316'], ['47', 'Orders', '#10b981'], ['23', 'Reserv.', '#8b5cf6']].map(([v, l, c]) => (
            <div key={l} style={{ padding: '8px 6px', borderRadius: 10, background: c + '15', border: `1px solid ${c}30`, textAlign: 'center' }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: c }}>{v}</div>
              <div style={{ fontSize: 9, color: '#888', marginTop: 2 }}>{l}</div>
            </div>
          ))}
        </div>
        {/* Mini bar chart */}
        <div style={{ fontSize: 10, color: '#888', marginBottom: 6 }}>Weekly Revenue</div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 60 }}>
          {[45, 72, 55, 88, 62, 95, 78].map((h, i) => (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
              <div style={{ width: '100%', height: `${h}%`, borderRadius: '4px 4px 0 0', background: i === 5 ? color : color + '40' }} />
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', marginTop: 2 }}>
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
            <span key={d} style={{ fontSize: 9, color: '#555' }}>{d}</span>
          ))}
        </div>
      </div>
    </div>
  );

  if (id === 'customers') return (
    <div className="mock">
      <div className="mock-header">
        <div className="mock-dot" /> <div className="mock-dot" /> <div className="mock-dot" />
        <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>Customer Management</span>
      </div>
      <div style={{ padding: '8px 12px' }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          {[['1.240', 'Customers', '#ec4899'], ['92%', 'Satisfaction', '#10b981']].map(([v, l, c]) => (
            <div key={l} style={{ flex: 1, padding: '8px', borderRadius: 10, background: c + '15', border: `1px solid ${c}30`, textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: c }}>{v}</div>
              <div style={{ fontSize: 9, color: '#888', marginTop: 2 }}>{l}</div>
            </div>
          ))}
        </div>
        {[
          { name: 'Sarah A.', pts: '4.820 pts', visits: '34 visits', avg: '₺185 avg.' },
          { name: 'Mike T.', pts: '3.150 pts', visits: '21 visits', avg: '₺220 avg.' },
          { name: 'Emma K.', pts: '2.700 pts', visits: '18 visits', avg: '₺160 avg.' },
        ].map((c) => (
          <div key={c.name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 8px', borderRadius: 10, background: '#ffffff05', marginBottom: 5 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: color + '25', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span className="material-symbols-outlined" style={{ color, fontSize: 16 }}>person</span>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>{c.name}</div>
              <div style={{ fontSize: 10, color: '#888' }}>{c.visits} · {c.avg}</div>
            </div>
            <div style={{ fontSize: 10, color, fontWeight: 700, whiteSpace: 'nowrap' }}>{c.pts}</div>
          </div>
        ))}
      </div>
    </div>
  );

  if (id === 'stock') return (
    <div className="mock">
      <div className="mock-header">
        <div className="mock-dot" /> <div className="mock-dot" /> <div className="mock-dot" />
        <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>Stok & Reçete Yönetimi</span>
      </div>
      <div style={{ padding: '8px 12px' }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          {[['₺14.250', 'Portföy Değeri', '#10b981'], ['%100', 'e-Fatura', '#3b82f6']].map(([v, l, c]) => (
            <div key={l} style={{ flex: 1, padding: '8px', borderRadius: 10, background: c + '15', border: `1px solid ${c}30`, textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: c }}>{v}</div>
              <div style={{ fontSize: 9, color: '#888', marginTop: 2 }}>{l}</div>
            </div>
          ))}
        </div>
        {[
          { name: 'Dana Kıyma', qty: '24,5 kg', cost: '₺380/kg', status: 'GİB Aktarıldı' },
          { name: 'Cheddar Peyniri', qty: '12,0 kg', cost: '₺240/kg', status: 'Reçete Bağlı' },
          { name: 'Zeytinyağı 5L', qty: '4 Teneke', cost: '₺1.200', status: 'Stok Tam' },
        ].map((s) => (
          <div key={s.name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 8px', borderRadius: 10, background: '#ffffff05', marginBottom: 5 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: color + '25', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span className="material-symbols-outlined" style={{ color, fontSize: 16 }}>inventory_2</span>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>{s.name}</div>
              <div style={{ fontSize: 10, color: '#888' }}>{s.qty} · {s.cost}</div>
            </div>
            <div style={{ fontSize: 9, color, fontWeight: 700, background: color + '15', padding: '3px 6px', borderRadius: 4 }}>{s.status}</div>
          </div>
        ))}
      </div>
    </div>
  );

  return null;
}

/* ─── MAIN COMPONENT ─── */
const HomePage = () => {
  const { t, i18n } = useTranslation();
  const reduceMotion = useReducedMotion();
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const languages = [
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  ];

  const changeLanguage = (lng) => { i18n.changeLanguage(lng); setShowLangMenu(false); };
  const currentLang = languages.find(l => l.code === (i18n.language?.split('-')[0] || 'tr')) || languages[0];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setPageSeo({
      title: 'QRezervasyon — QR Menü, Rezervasyon ve Stok Yönetimi',
      description: 'Restoranlar için QR menü, masa rezervasyonu, sipariş ve stok yönetimi. Menünüzü dakikalar içinde dijitalleştirin, ücretsiz deneyin.',
      canonicalPath: '/',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'QRezervasyon',
        url: 'https://qrezervasyon.com.tr/',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        description: 'Restoranlar için QR menü, masa rezervasyonu, sipariş ve stok yönetimi platformu.',
        inLanguage: 'tr',
        publisher: {
          '@type': 'Organization',
          name: 'QRezervasyon',
          url: 'https://qrezervasyon.com.tr/',
          logo: 'https://qrezervasyon.com.tr/logo512.png'
        }
      },
      jsonLdId: 'homepage-jsonld'
    });
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const FEATURES = [
    {
      id: 'qrmenu',
      icon: 'qr_code_scanner',
      color: '#f97316',
      title: t('landing.features.qrmenu_title'),
      subtitle: t('landing.features.qrmenu_subtitle'),
      desc: t('landing.features.qrmenu_desc'),
      bullets: [
        t('landing.features.qrmenu_b1'),
        t('landing.features.qrmenu_b2'),
        t('landing.features.qrmenu_b3'),
        t('landing.features.qrmenu_b4')
      ],
      badge: t('landing.features.qrmenu_badge'),
    },
    {
      id: 'stock',
      icon: 'inventory_2',
      color: '#10b981',
      title: t('landing.features.stock_title', 'Stok, Reçete ve Otomatik Fatura Çekimi'),
      subtitle: t('landing.features.stock_subtitle', 'GİB e-Fatura Aktarımı, Gramajlı Reçeteler & Hızlı Stok Sayımı'),
      desc: t('landing.features.stock_desc', 'Metro, Bizim Toptan ve tedarikçi e-faturalarını GİB portalından otomatik çekin. Gramajlı reçeteler ile sipariş anında otomatik stok düşüşü yapın, zayiatı engelleyin.'),
      bullets: [
        t('landing.features.stock_b1', 'GİB portaldan otomatik e-fatura çekimi & PDF/XML aktarımı'),
        t('landing.features.stock_b2', 'Gramajlı porsiyon reçeteleri & anlık kâr marjı hesabı'),
        t('landing.features.stock_b3', 'Kamera ile hızlı barkodlu stok sayımı & sayım fark raporu'),
        t('landing.features.stock_b4', 'Alt reçete (sos/hamur) & minimum stok seviyesi uyarıları')
      ],
      badge: 'YENİ & MALİYET TASARRUFU',
      link: '/stok-yonetimi'
    },
    {
      id: 'reservations',
      icon: 'event_available',
      color: '#8b5cf6',
      title: t('landing.features.reservations_title'),
      subtitle: t('landing.features.reservations_subtitle'),
      desc: t('landing.features.reservations_desc'),
      bullets: [
        t('landing.features.reservations_b1'),
        t('landing.features.reservations_b2'),
        t('landing.features.reservations_b3'),
        t('landing.features.reservations_b4')
      ],
      badge: null,
    },
    {
      id: 'kitchen',
      icon: 'skillet',
      color: '#10b981',
      title: t('landing.features.kitchen_title'),
      subtitle: t('landing.features.kitchen_subtitle'),
      desc: t('landing.features.kitchen_desc'),
      bullets: [
        t('landing.features.kitchen_b1'),
        t('landing.features.kitchen_b2'),
        t('landing.features.kitchen_b3'),
        t('landing.features.kitchen_b4')
      ],
      badge: null,
    },
    {
      id: 'pos',
      icon: 'point_of_sale',
      color: '#3b82f6',
      title: t('landing.features.pos_title'),
      subtitle: t('landing.features.pos_subtitle'),
      desc: t('landing.features.pos_desc'),
      bullets: [
        t('landing.features.pos_b1'),
        t('landing.features.pos_b2'),
        t('landing.features.pos_b3'),
        t('landing.features.pos_b4')
      ],
      badge: null,
    },
    {
      id: 'analytics',
      icon: 'analytics',
      color: '#f59e0b',
      title: t('landing.features.analytics_title'),
      subtitle: t('landing.features.analytics_subtitle'),
      desc: t('landing.features.analytics_desc'),
      bullets: [
        t('landing.features.analytics_b1'),
        t('landing.features.analytics_b2'),
        t('landing.features.analytics_b3'),
        t('landing.features.analytics_b4')
      ],
      badge: null,
    },
    {
      id: 'customers',
      icon: 'group',
      color: '#ec4899',
      title: t('landing.features.customers_title'),
      subtitle: t('landing.features.customers_subtitle'),
      desc: t('landing.features.customers_desc'),
      bullets: [
        t('landing.features.customers_b1'),
        t('landing.features.customers_b2'),
        t('landing.features.customers_b3'),
        t('landing.features.customers_b4')
      ],
      badge: null,
    },
  ];

  const STATS = [
    { value: '2.500+', label: t('landing.stat_active_restaurants'), icon: 'store' },
    { value: '₺180M+', label: t('landing.stat_processed_orders'), icon: 'payments' },
    { value: '4.8★', label: t('landing.stat_customer_satisfaction'), icon: 'star' },
    { value: '99.9%', label: t('landing.stat_uptime_guarantee'), icon: 'verified' },
  ];

  const PLANS = [
    {
      name: t('landing.plans.starter_name'),
      price: '₺299',
      period: '/ay',
      color: '#64748b',
      features: [
        t('landing.plans.starter_f1'), 
        t('landing.plans.starter_f2'), 
        t('landing.plans.starter_f3'), 
        t('landing.plans.starter_f4')
      ],
      cta: t('landing.plans.starter_cta'),
      popular: false,
    },
    {
      name: t('landing.plans.pro_name'),
      price: '₺699',
      period: '/ay',
      color: '#f97316',
      features: [
        t('landing.plans.pro_f1'),
        t('landing.plans.pro_f2'),
        t('landing.plans.pro_f3'),
        t('landing.plans.pro_f4'),
        t('landing.plans.pro_f5'),
        t('landing.plans.pro_f6')
      ],
      cta: t('landing.plans.pro_cta'),
      popular: true,
    },
    {
      name: t('landing.plans.enterprise_name'),
      price: t('landing.plans.enterprise_price'),
      period: '',
      color: '#8b5cf6',
      features: [
        t('landing.plans.enterprise_f1'),
        t('landing.plans.enterprise_f2'),
        t('landing.plans.enterprise_f3'),
        t('landing.plans.enterprise_f4'),
        t('landing.plans.enterprise_f5')
      ],
      cta: t('landing.plans.enterprise_cta'),
      popular: false,
    },
  ];

  const FAQS = [
    { q: t('landing.faq.q1'), a: t('landing.faq.a1') },
    { q: t('landing.faq.q2'), a: t('landing.faq.a2') },
    { q: t('landing.faq.q3'), a: t('landing.faq.a3') },
    { q: t('landing.faq.q4'), a: t('landing.faq.a4') },
    { q: t('landing.faq.q5'), a: t('landing.faq.a5') },
    { q: t('landing.faq.q6'), a: t('landing.faq.a6') },
  ];

  useEffect(() => {
    setPageSeo({
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a }
        }))
      },
      jsonLdId: 'faq-jsonld'
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  return (
    <div className="lp-root">
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        
        .lp-root {
          font-family: 'Inter', system-ui, sans-serif;
          background: #0d1117;
          color: #e2e8f0;
          min-height: 100vh;
          overflow-x: hidden;
        }

        /* ── NAVBAR ── */
        .lp-nav {
          position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
          display: flex; align-items: center; justify-content: space-between;
          padding: max(env(safe-area-inset-top, 0px), 14px) clamp(16px, 4vw, 48px) 14px;
          min-height: calc(72px + env(safe-area-inset-top, 0px));
          transition: all 0.3s ease;
        }
        .lp-nav.scrolled {
          background: rgba(13, 17, 23, 0.95);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(255,255,255,0.07);
          box-shadow: 0 4px 32px rgba(0,0,0,0.4);
        }
        .nav-logo {
          display: flex; align-items: center; gap: 10px;
          text-decoration: none; color: inherit;
        }
        .nav-logo-icon {
          width: 42px; height: 42px; border-radius: 12px;
          background: linear-gradient(135deg, #f97316, #ea580c);
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 4px 16px rgba(249,115,22,0.35);
        }
        .nav-logo-text { font-size: 20px; font-weight: 800; }
        .nav-logo-text span { color: #f97316; }
        .nav-links {
          display: flex; align-items: center; gap: 4px;
          list-style: none;
        }
        .nav-links a {
          color: rgba(255,255,255,0.65); text-decoration: none;
          font-size: 14px; font-weight: 500;
          padding: 8px 14px; border-radius: 10px;
          transition: all 0.2s;
        }
        .nav-links a:hover { color: #fff; background: rgba(255,255,255,0.07); }
        .nav-actions { display: flex; align-items: center; gap: 10px; }
        .btn-nav-login {
          font-size: 14px; font-weight: 600; padding: 9px 20px; border-radius: 10px;
          background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1);
          color: #fff; cursor: pointer; text-decoration: none; transition: all 0.2s;
          display: flex; align-items: center; gap: 6px;
        }
        .btn-nav-login:hover { background: rgba(255,255,255,0.12); }
        .btn-nav-register {
          font-size: 14px; font-weight: 700; padding: 9px 20px; border-radius: 10px;
          background: linear-gradient(135deg, #f97316, #ea580c); border: none;
          color: #fff; cursor: pointer; text-decoration: none; transition: all 0.2s;
          display: flex; align-items: center; gap: 6px;
          box-shadow: 0 4px 16px rgba(249,115,22,0.3);
        }
        .btn-nav-register:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(249,115,22,0.45); }

        /* ── LANG SELECTOR ── */
        .lang-btn {
          display: flex; align-items: center; gap: 6px;
          padding: 8px 12px; border-radius: 10px;
          background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1);
          color: #fff; cursor: pointer; font-size: 13px; font-weight: 600;
          transition: all 0.2s; position: relative;
        }
        .lang-btn:hover { background: rgba(255,255,255,0.1); }
        .lang-dropdown {
          position: absolute; top: calc(100% + 8px); right: 0;
          background: #1a1f2e; border: 1px solid rgba(255,255,255,0.1);
          border-radius: 14px; padding: 8px; min-width: 180px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.5);
          animation: fadeSlideDown 0.2s ease;
        }
        @keyframes fadeSlideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .lang-option {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 12px; border-radius: 10px; width: 100%;
          background: none; border: none; color: rgba(255,255,255,0.6);
          cursor: pointer; font-size: 13px; font-weight: 500;
          transition: all 0.15s;
        }
        .lang-option:hover { background: rgba(255,255,255,0.07); color: #fff; }
        .lang-option.active { background: rgba(249,115,22,0.12); color: #f97316; font-weight: 700; }

        /* ── HERO ── */
        .lp-hero {
          position: relative; min-height: 100vh;
          display: flex; align-items: center; justify-content: center;
          text-align: center; padding: 120px 24px 80px;
          overflow: hidden;
        }
        .hero-bg-orb {
          position: absolute; border-radius: 50%; pointer-events: none;
          filter: blur(120px);
        }
        .hero-orb-1 { width: 700px; height: 700px; top: -200px; left: -200px; background: rgba(249,115,22,0.08); }
        .hero-orb-2 { width: 500px; height: 500px; bottom: -150px; right: -100px; background: rgba(139,92,246,0.06); }
        .hero-orb-3 { width: 400px; height: 400px; top: 50%; left: 50%; transform: translate(-50%,-50%); background: rgba(249,115,22,0.04); }
        .hero-grid {
          position: absolute; inset: 0;
          background-image: linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px);
          background-size: 60px 60px;
          mask-image: radial-gradient(ellipse at center, black 30%, transparent 80%);
        }
        .hero-content { position: relative; z-index: 2; max-width: 900px; }
        .hero-eyebrow {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 8px 20px; border-radius: 100px;
          background: rgba(249,115,22,0.12); border: 1px solid rgba(249,115,22,0.25);
          font-size: 13px; font-weight: 600; color: #f97316; margin-bottom: 28px;
          letter-spacing: 0.05em;
        }
        .hero-title {
          font-size: clamp(2.8rem, 7vw, 5.5rem);
          font-weight: 900; line-height: 1.05; letter-spacing: -0.03em;
          color: #fff; margin-bottom: 24px;
        }
        .hero-title .gradient {
          background: linear-gradient(135deg, #f97316, #f59e0b);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .hero-subtitle {
          font-size: clamp(1rem, 2vw, 1.25rem);
          color: rgba(255,255,255,0.55); line-height: 1.7; max-width: 600px;
          margin: 0 auto 44px;
        }
        .hero-cta-group { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }
        .btn-primary {
          display: inline-flex; align-items: center; gap: 10px;
          padding: 16px 36px; border-radius: 16px; font-size: 16px; font-weight: 700;
          background: linear-gradient(135deg, #f97316, #ea580c);
          color: #fff; text-decoration: none; border: none; cursor: pointer;
          box-shadow: 0 8px 32px rgba(249,115,22,0.35);
          transition: all 0.25s;
        }
        .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 12px 40px rgba(249,115,22,0.5); }
        .btn-secondary {
          display: inline-flex; align-items: center; gap: 10px;
          padding: 16px 36px; border-radius: 16px; font-size: 16px; font-weight: 600;
          background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12);
          color: #fff; text-decoration: none; cursor: pointer;
          transition: all 0.25s;
        }
        .btn-secondary:hover { background: rgba(255,255,255,0.12); transform: translateY(-2px); }
        .hero-social-proof {
          margin-top: 56px; display: flex; align-items: center; justify-content: center;
          gap: 20px; flex-wrap: wrap;
        }
        .social-proof-item {
          display: flex; align-items: center; gap: 8px;
          font-size: 13px; color: rgba(255,255,255,0.45);
        }
        .social-proof-item .material-symbols-outlined { font-size: 18px; color: #f97316; }

        /* ── STATS ── */
        .lp-stats { padding: 80px 24px; }
        .stats-inner {
          max-width: 1100px; margin: 0 auto;
          display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 20px;
        }
        .stat-card {
          padding: 32px; border-radius: 20px;
          background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07);
          text-align: center; transition: all 0.3s;
        }
        .stat-card:hover { background: rgba(255,255,255,0.05); border-color: rgba(249,115,22,0.2); transform: translateY(-4px); }
        .stat-icon { font-size: 2rem; color: #f97316; margin-bottom: 12px; }
        .stat-value { font-size: 2.5rem; font-weight: 900; color: #fff; line-height: 1; }
        .stat-label { font-size: 13px; color: rgba(255,255,255,0.45); margin-top: 8px; }

        /* ── SECTION LABEL ── */
        .section-eyebrow {
          text-align: center; margin-bottom: 16px;
          display: flex; justify-content: center;
        }
        .eyebrow-pill {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 6px 18px; border-radius: 100px;
          background: rgba(249,115,22,0.1); border: 1px solid rgba(249,115,22,0.2);
          font-size: 12px; font-weight: 700; color: #f97316; letter-spacing: 0.1em; text-transform: uppercase;
        }
        .section-title {
          text-align: center; font-size: clamp(2rem, 4vw, 3rem);
          font-weight: 900; color: #fff; letter-spacing: -0.02em;
          margin-bottom: 16px;
        }
        .section-subtitle {
          text-align: center; font-size: 16px; color: rgba(255,255,255,0.45);
          max-width: 560px; margin: 0 auto 72px; line-height: 1.7;
        }

        /* ── FEATURES ── */
        .lp-features { padding: 80px 24px; }
        .features-inner { max-width: 1140px; margin: 0 auto; }
        .feature-row {
          display: flex; align-items: center; gap: 64px;
          margin-bottom: 100px;
        }
        .feature-text { flex: 1; min-width: 0; }
        .feature-badge {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 5px 14px; border-radius: 100px; font-size: 11px; font-weight: 700;
          letter-spacing: 0.05em; margin-bottom: 20px;
        }
        .feature-icon-wrap {
          width: 60px; height: 60px; border-radius: 18px;
          display: flex; align-items: center; justify-content: center; margin-bottom: 20px;
        }
        .feature-title {
          font-size: clamp(1.8rem, 3vw, 2.4rem); font-weight: 900;
          color: #fff; letter-spacing: -0.02em; margin-bottom: 8px;
        }
        .feature-subtitle { font-size: 14px; font-weight: 600; margin-bottom: 16px; }
        .feature-desc { font-size: 15px; color: rgba(255,255,255,0.5); line-height: 1.8; margin-bottom: 28px; }
        .feature-bullets { list-style: none; display: flex; flex-direction: column; gap: 12px; }
        .feature-bullets li {
          display: flex; align-items: center; gap: 12px;
          font-size: 14px; color: rgba(255,255,255,0.7); font-weight: 500;
        }
        .bullet-icon { font-size: 18px !important; color: var(--bullet-color); flex-shrink: 0; }
        .feature-screen-wrap { flex: 1; position: relative; min-width: 0; }
        .feature-screen {
          border-radius: 20px; overflow: hidden;
          background: #161b27; border: 1px solid rgba(255,255,255,0.1);
          box-shadow: 0 24px 80px rgba(0,0,0,0.5);
          position: relative; z-index: 1;
          min-height: 340px;
          display: flex; flex-direction: column;
        }
        .screen-glow {
          position: absolute; inset: -30px; border-radius: 30px;
          filter: blur(60px); z-index: 0; opacity: 0.4;
        }

        /* ── MOCK ── */
        .mock { width: 100%; flex: 1; display: flex; flex-direction: column; }
        .mock-qrmenu { width: 100%; flex: 1; display: flex; flex-direction: column; }
        .mock-header {
          display: flex; align-items: center; gap: 6px;
          padding: 10px 16px; border-bottom: 1px solid rgba(255,255,255,0.07);
          background: rgba(255,255,255,0.02);
        }
        .mock-dot { width: 10px; height: 10px; border-radius: 50%; background: rgba(255,255,255,0.15); }

        /* ── PRICING ── */
        .lp-pricing { padding: 80px 24px; }
        .pricing-grid {
          max-width: 1000px; margin: 0 auto;
          display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 24px;
        }
        .pricing-card {
          border-radius: 24px; padding: 36px 28px;
          background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08);
          position: relative; transition: all 0.3s;
        }
        .pricing-card:hover { transform: translateY(-6px); }
        .pricing-card.popular {
          background: rgba(249,115,22,0.06);
          border-color: rgba(249,115,22,0.35);
          box-shadow: 0 0 0 1px rgba(249,115,22,0.15), 0 24px 60px rgba(249,115,22,0.15);
        }
        .popular-badge {
          position: absolute; top: -13px; left: 50%; transform: translateX(-50%);
          background: linear-gradient(135deg, #f97316, #ea580c);
          color: #fff; font-size: 11px; font-weight: 800;
          padding: 5px 18px; border-radius: 100px; white-space: nowrap; letter-spacing: 0.05em;
        }
        .plan-name { font-size: 16px; font-weight: 700; color: rgba(255,255,255,0.6); margin-bottom: 16px; text-transform: uppercase; letter-spacing: 0.08em; }
        .plan-price { font-size: 3rem; font-weight: 900; color: #fff; line-height: 1; }
        .plan-price span { font-size: 1rem; font-weight: 500; color: rgba(255,255,255,0.4); }
        .plan-features { list-style: none; margin: 28px 0 32px; display: flex; flex-direction: column; gap: 12px; }
        .plan-features li { display: flex; align-items: center; gap: 10px; font-size: 14px; color: rgba(255,255,255,0.65); }
        .plan-features li .material-symbols-outlined { font-size: 18px; color: #10b981; flex-shrink: 0; }
        .btn-plan {
          display: block; width: 100%; padding: 14px; border-radius: 14px;
          font-size: 15px; font-weight: 700; text-align: center; cursor: pointer;
          text-decoration: none; border: none; transition: all 0.25s;
        }
        .btn-plan.primary {
          background: linear-gradient(135deg, #f97316, #ea580c); color: #fff;
          box-shadow: 0 6px 24px rgba(249,115,22,0.35);
        }
        .btn-plan.primary:hover { transform: translateY(-2px); box-shadow: 0 10px 32px rgba(249,115,22,0.5); }
        .btn-plan.secondary {
          background: rgba(255,255,255,0.06); color: rgba(255,255,255,0.7);
          border: 1px solid rgba(255,255,255,0.1);
        }
        .btn-plan.secondary:hover { background: rgba(255,255,255,0.12); color: #fff; }

        /* ── FAQ ── */
        .lp-faq { padding: 80px 24px; }
        .faq-list { max-width: 760px; margin: 0 auto; display: flex; flex-direction: column; gap: 12px; }
        .faq-item {
          border: 1px solid rgba(255,255,255,0.08); border-radius: 16px;
          background: rgba(255,255,255,0.02); overflow: hidden;
        }
        .faq-question {
          width: 100%; display: flex; align-items: center; justify-content: space-between;
          gap: 16px; padding: 20px 24px; background: none; border: none; cursor: pointer;
          text-align: left; font-size: 15px; font-weight: 700; color: #fff;
        }
        .faq-question .material-symbols-outlined { transition: transform 0.25s; color: rgba(255,255,255,0.5); flex-shrink: 0; }
        .faq-item.open .faq-question .material-symbols-outlined { transform: rotate(180deg); color: #f97316; }
        .faq-answer {
          font-size: 14px; line-height: 1.7; color: rgba(255,255,255,0.6);
        }

        /* ── CTA BAND ── */
        .lp-cta-band {
          padding: 80px 24px; margin: 40px 24px;
          border-radius: 32px;
          background: linear-gradient(135deg, rgba(249,115,22,0.15), rgba(234,88,12,0.08));
          border: 1px solid rgba(249,115,22,0.2);
          text-align: center;
          position: relative; overflow: hidden;
        }
        .cta-orb {
          position: absolute; pointer-events: none; border-radius: 50%;
          filter: blur(80px);
        }
        .cta-orb-1 { width: 400px; height: 400px; left: -100px; top: -100px; background: rgba(249,115,22,0.12); }
        .cta-orb-2 { width: 300px; height: 300px; right: -50px; bottom: -50px; background: rgba(139,92,246,0.08); }
        .cta-title { font-size: clamp(2rem, 4vw, 3.2rem); font-weight: 900; color: #fff; margin-bottom: 16px; letter-spacing: -0.02em; }
        .cta-sub { font-size: 17px; color: rgba(255,255,255,0.55); max-width: 480px; margin: 0 auto 40px; line-height: 1.7; }
        .cta-btns { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; }

        /* ── FOOTER ── */
        .lp-footer {
          padding: 60px 24px 40px;
          border-top: 1px solid rgba(255,255,255,0.06);
        }
        .footer-inner { max-width: 1140px; margin: 0 auto; }
        .footer-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 40px; flex-wrap: wrap; margin-bottom: 48px; }
        .footer-brand { max-width: 280px; }
        .footer-brand-logo { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; }
        .footer-desc { font-size: 14px; color: rgba(255,255,255,0.4); line-height: 1.7; }
        .footer-links-col h4 { font-size: 13px; font-weight: 700; color: rgba(255,255,255,0.5); text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 16px; }
        .footer-links-col a { display: block; font-size: 14px; color: rgba(255,255,255,0.45); text-decoration: none; margin-bottom: 10px; transition: color 0.2s; }
        .footer-links-col a:hover { color: #fff; }
        .footer-bottom { display: flex; justify-content: space-between; align-items: center; padding-top: 24px; border-top: 1px solid rgba(255,255,255,0.06); flex-wrap: wrap; gap: 12px; }
        .footer-copy { font-size: 13px; color: rgba(255,255,255,0.3); }
        .footer-badge { display: flex; align-items: center; gap: 6px; font-size: 12px; color: rgba(255,255,255,0.3); }

        /* ── MOBILE MENU ── */
        .hamburger { display: none; background: none; border: none; cursor: pointer; padding: 10px; border-radius: 8px; }
        .hamburger span { display: block; width: 24px; height: 2.5px; background: #fff; margin: 5px 0; border-radius: 2px; transition: all 0.3s; }
        .mobile-nav {
          display: none; position: fixed; top: calc(72px + env(safe-area-inset-top, 0px)); left: 0; right: 0;
          background: rgba(13,17,23,0.98); backdrop-filter: blur(25px);
          border-bottom: 1px solid rgba(255,255,255,0.1);
          padding: 20px 24px 32px; z-index: 999;
          max-height: calc(100vh - 72px - env(safe-area-inset-top, 0px));
          overflow-y: auto;
        }
        .mobile-nav.open { display: block; }
        .mobile-nav a { display: block; padding: 14px 0; color: rgba(255,255,255,0.85); text-decoration: none; font-size: 16px; border-bottom: 1px solid rgba(255,255,255,0.06); font-weight: 600; }
        .mobile-nav .mobile-cta { display: flex; flex-direction: column; gap: 10px; margin-top: 20px; }

        @media (max-width: 900px) {
          .lp-nav {
            padding-top: max(calc(env(safe-area-inset-top, 0px) + 12px), 20px);
            padding-bottom: 12px;
          }
          .nav-links { display: none; }
          .hamburger { display: block; }
          .btn-nav-login, .btn-nav-register { display: none; }
          .feature-row { flex-direction: column !important; gap: 36px; }
          .feature-screen-wrap { width: 100%; }
        }
        @media (max-width: 600px) {
          .hero-title { font-size: 2.6rem; }
          .lp-cta-band { margin: 20px 12px; }
        }
      `}</style>

      {/* ── NAVBAR ── */}
      <nav className={`lp-nav ${scrolled ? 'scrolled' : ''}`}>
        <a href="#hero" className="nav-logo" onClick={e => { e.preventDefault(); scrollTo('hero'); }}>
          <div className="nav-logo-icon">
            <span className="material-symbols-outlined" style={{ color: '#fff', fontSize: 22 }}>restaurant</span>
          </div>
          <span className="nav-logo-text">QR<span>ezervasyon</span></span>
        </a>

        <ul className="nav-links">
          {[[t('landing.nav_features'), 'features'], [t('landing.nav_pricing'), 'pricing'], [t('landing.nav_contact'), 'contact']].map(([label, id]) => (
            <li key={id}>
              <a href={`#${id}`} onClick={e => { e.preventDefault(); scrollTo(id); }}>{label}</a>
            </li>
          ))}
          <li>
            <Link to="/stok-yonetimi" style={{ color: '#10b981', fontWeight: 700 }}>
              Stok & Reçete
            </Link>
          </li>
        </ul>

        <div className="nav-actions">
          {/* Language Selector */}
          <div style={{ position: 'relative' }}>
            <button className="lang-btn" onClick={() => setShowLangMenu(v => !v)}>
              <span style={{ fontSize: 18 }}>{currentLang.flag}</span>
              <span style={{ fontSize: 12 }}>{currentLang.code.toUpperCase()}</span>
              <span className="material-symbols-outlined" style={{ fontSize: 16, opacity: 0.5 }}>expand_more</span>
            </button>
            {showLangMenu && (
              <div className="lang-dropdown">
                {languages.map(lang => (
                  <button
                    key={lang.code}
                    className={`lang-option ${i18n.language?.startsWith(lang.code) ? 'active' : ''}`}
                    onClick={() => changeLanguage(lang.code)}
                  >
                    <span style={{ fontSize: 18 }}>{lang.flag}</span>
                    <span>{lang.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <Link to="/login" className="btn-nav-login">
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>login</span>
            {t('landing.nav_login')}
          </Link>
          <Link to="/register" className="btn-nav-register">
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>person_add</span>
            {t('landing.nav_register')}
          </Link>

          <button className="hamburger" onClick={() => setMobileMenuOpen(v => !v)}>
            <span /> <span /> <span />
          </button>
        </div>
      </nav>

      {/* Mobile Nav */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="mobile-nav open"
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.25, ease: EASE_OUT_EXPO }}
          >
            {[[t('landing.nav_features'), 'features'], [t('landing.nav_pricing'), 'pricing'], [t('landing.nav_contact'), 'contact']].map(([label, id]) => (
              <a key={id} href={`#${id}`} onClick={e => { e.preventDefault(); scrollTo(id); }}>{label}</a>
            ))}
            <div className="mobile-cta">
              <Link to="/login" className="btn-nav-login" style={{ flex: 1, justifyContent: 'center' }}>{t('landing.nav_login')}</Link>
              <Link to="/register" className="btn-nav-register" style={{ flex: 1, justifyContent: 'center' }}>{t('landing.nav_register')}</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── HERO ── */}
      <section id="hero" className="lp-hero">
        <motion.div
          className="hero-bg-orb hero-orb-1"
          animate={reduceMotion ? undefined : { y: [0, -28, 0], x: [0, 18, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="hero-bg-orb hero-orb-2"
          animate={reduceMotion ? undefined : { y: [0, 24, 0], x: [0, -16, 0] }}
          transition={{ duration: 17, repeat: Infinity, ease: 'easeInOut' }}
        />
        <div className="hero-bg-orb hero-orb-3" />
        <div className="hero-grid" />

        <motion.div
          className="hero-content"
          variants={staggerContainer(0.1, 0.05)}
          initial="hidden"
          animate="show"
        >
          <motion.div className="hero-eyebrow" variants={fadeUp}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>restaurant</span>
            {t('landing.hero_eyebrow')}
          </motion.div>

          <motion.h1 className="hero-title" variants={fadeUp}>
            {t('landing.hero_title1')}<br />
            <span className="gradient">{t('landing.hero_title2')}</span><br />
            {t('landing.hero_title3')}
          </motion.h1>

          <motion.p className="hero-subtitle" variants={fadeUp}>
            {t('landing.hero_subtitle')}
          </motion.p>

          <motion.div className="hero-cta-group" variants={fadeUp}>
            <MotionLink
              to="/register"
              className="btn-primary"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.97 }}
            >
              <span className="material-symbols-outlined">rocket_launch</span>
              {t('landing.hero_btn_start')}
            </MotionLink>
            <motion.button
              className="btn-secondary"
              onClick={() => scrollTo('features')}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.97 }}
            >
              <span className="material-symbols-outlined">play_circle</span>
              {t('landing.hero_btn_explore')}
            </motion.button>
          </motion.div>

          <motion.div className="hero-social-proof" variants={fadeUp}>
            <div className="social-proof-item">
              <span className="material-symbols-outlined">verified</span>
              {t('landing.hero_proof1')}
            </div>
            <div className="social-proof-item" style={{ color: 'rgba(255,255,255,0.25)' }}>•</div>
            <div className="social-proof-item">
              <span className="material-symbols-outlined">support_agent</span>
              {t('landing.hero_proof2')}
            </div>
            <div className="social-proof-item" style={{ color: 'rgba(255,255,255,0.25)' }}>•</div>
            <div className="social-proof-item">
              <span className="material-symbols-outlined">update</span>
              {t('landing.hero_proof3')}
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* ── STATS ── */}
      <section className="lp-stats">
        <motion.div
          className="stats-inner"
          variants={staggerContainer(0.08)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
        >
          {STATS.map((s, i) => (
            <motion.div className="stat-card" key={i} variants={fadeUp} whileHover={{ y: -4 }}>
              <div className="stat-icon">
                <span className="material-symbols-outlined">{s.icon}</span>
              </div>
              <div className="stat-value"><AnimatedCounter value={s.value} /></div>
              <div className="stat-label">{s.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" className="lp-features">
        <div className="section-eyebrow">
          <span className="eyebrow-pill">
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>apps</span>
            {t('landing.feat_eyebrow')}
          </span>
        </div>
        <h2 className="section-title">{t('landing.feat_title1')}<br />{t('landing.feat_title2')}</h2>
        <p className="section-subtitle">
          {t('landing.feat_subtitle')}
        </p>

        <div className="features-inner">
          {FEATURES.map((feature, index) => (
            <FeatureCard key={feature.id} feature={feature} index={index} />
          ))}
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="lp-pricing">
        <div className="section-eyebrow">
          <span className="eyebrow-pill">
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>payments</span>
            {t('landing.pricing_eyebrow')}
          </span>
        </div>
        <h2 className="section-title">{t('landing.pricing_title')}</h2>
        <p className="section-subtitle">
          {t('landing.pricing_subtitle')}
        </p>

        <motion.div
          className="pricing-grid"
          variants={staggerContainer(0.1)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
        >
          {PLANS.map((plan, i) => (
            <motion.div
              key={plan.name}
              className={`pricing-card ${plan.popular ? 'popular' : ''}`}
              variants={fadeUp}
              whileHover={{ y: -8 }}
              transition={{ duration: 0.3, ease: EASE_OUT_EXPO }}
            >
              {plan.popular && <div className="popular-badge">{t('landing.pricing_popular_badge')}</div>}
              <div className="plan-name">{plan.name}</div>
              <div className="plan-price">
                {plan.price}
                {plan.period && <span>{plan.period}</span>}
              </div>
              <ul className="plan-features">
                {plan.features.map((f) => (
                  <li key={f}>
                    <span className="material-symbols-outlined">check_circle</span>
                    {f}
                  </li>
                ))}
              </ul>
              <MotionLink
                to={i === 2 ? '#contact' : '/register'}
                className={`btn-plan ${plan.popular ? 'primary' : 'secondary'}`}
                onClick={i === 2 ? (e) => { e.preventDefault(); scrollTo('contact'); } : undefined}
                whileTap={{ scale: 0.97 }}
              >
                {plan.cta}
              </MotionLink>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="lp-faq">
        <div className="section-eyebrow">
          <span className="eyebrow-pill">
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>help</span>
            {t('landing.faq_eyebrow')}
          </span>
        </div>
        <h2 className="section-title">{t('landing.faq_title')}</h2>
        <motion.div
          className="faq-list"
          variants={staggerContainer(0.06)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
        >
          {FAQS.map((item, i) => (
            <motion.div key={item.q} className={`faq-item ${openFaq === i ? 'open' : ''}`} variants={fadeUp}>
              <button
                type="button"
                className="faq-question"
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                aria-expanded={openFaq === i}
              >
                {item.q}
                <span className="material-symbols-outlined">expand_more</span>
              </button>
              <AnimatePresence initial={false}>
                {openFaq === i && (
                  <motion.div
                    className="faq-answer"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: EASE_OUT_EXPO }}
                    style={{ overflow: 'hidden' }}
                  >
                    <div style={{ padding: '0 24px 22px' }}>{item.a}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ── CTA BAND ── */}
      <motion.div
        className="lp-cta-band"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.7, ease: EASE_OUT_EXPO }}
      >
        <div className="cta-orb cta-orb-1" />
        <div className="cta-orb cta-orb-2" />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h2 className="cta-title">{t('landing.cta_title')}</h2>
          <p className="cta-sub">
            {t('landing.cta_subtitle')}
          </p>
          <div className="cta-btns">
            <MotionLink
              to="/register"
              className="btn-primary"
              style={{ fontSize: 17, padding: '18px 42px' }}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.97 }}
            >
              <span className="material-symbols-outlined">rocket_launch</span>
              {t('landing.cta_btn_start')}
            </MotionLink>
            <MotionLink
              to="/login"
              className="btn-secondary"
              style={{ fontSize: 17, padding: '18px 42px' }}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.97 }}
            >
              <span className="material-symbols-outlined">login</span>
              {t('landing.cta_btn_login')}
            </MotionLink>
          </div>
        </div>
      </motion.div>

      {/* ── FOOTER ── */}
      <footer id="contact" className="lp-footer">
        <div className="footer-inner">
          <div className="footer-top">
            <div className="footer-brand">
              <div className="footer-brand-logo">
                <div className="nav-logo-icon">
                  <span className="material-symbols-outlined" style={{ color: '#fff', fontSize: 22 }}>restaurant</span>
                </div>
                <span className="nav-logo-text">QR<span>ezervasyon</span></span>
              </div>
              <p className="footer-desc">
                {t('landing.footer_desc')}
              </p>
            </div>
            <div className="footer-links-col">
              <h4>{t('landing.footer_col1_title')}</h4>
              <a href="#features" onClick={e => { e.preventDefault(); scrollTo('features'); }}>{t('landing.nav_features')}</a>
              <a href="#pricing" onClick={e => { e.preventDefault(); scrollTo('pricing'); }}>{t('landing.nav_pricing')}</a>
              <Link to="/register">{t('landing.nav_register')}</Link>
              <Link to="/login">{t('landing.nav_login')}</Link>
            </div>
            <div className="footer-links-col">
              <h4>{t('landing.footer_col2_title')}</h4>
              <Link to="/stok-yonetimi" style={{ color: '#10b981', fontWeight: 600 }}>Stok & Reçete (Otomatik Fatura)</Link>
              <a href="#features">{t('landing.features.qrmenu_title')}</a>
              <a href="#features">{t('landing.features.reservations_title')}</a>
              <a href="#features">{t('landing.features.kitchen_title')}</a>
              <a href="#features">{t('landing.features.pos_title')}</a>
              <a href="#features">{t('landing.features.analytics_title')}</a>
            </div>
            <div className="footer-links-col">
              <h4>{t('landing.footer_col3_title')}</h4>
              <a href="mailto:info@qrezervasyon.com.tr">info@qrezervasyon.com.tr</a>
              <a href="https://qrezervasyon.com.tr" target="_blank" rel="noopener noreferrer">qrezervasyon.com.tr</a>
            </div>
            <div className="footer-links-col">
              <h4>{t('landing.footer_legal_title')}</h4>
              <Link to="/kvkk">{t('landing.footer_legal_kvkk')}</Link>
              <Link to="/gizlilik-politikasi">{t('landing.footer_legal_privacy')}</Link>
              <Link to="/cerez-politikasi">{t('landing.footer_legal_cookies')}</Link>
              <Link to="/kullanim-sartlari">{t('landing.footer_legal_terms')}</Link>
              <Link to="/mesafeli-satis-sozlesmesi">{t('landing.footer_legal_distance_sale')}</Link>
            </div>
          </div>
          <div className="footer-bottom">
            <span className="footer-copy">{t('landing.footer_rights')}</span>
            <div className="footer-badge">
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>lock</span>
              {t('landing.footer_ssl')}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
